import ToastContent from "@/components/common/toast/ToastContent";
import keycloak, { configToUse } from "@/Keycloak";
import axios, { isAxiosError, AxiosError } from "axios";
import { logout } from "@/utils/logout";
import posthog from "posthog-js";
import { toast } from "sonner";
import { POSTHOG_ALLOWED_HOSTS } from "@/providers/PostHogProviderWrapper";
import { useGlobalStore } from "@/GlobalStore";

// Extend AxiosError to include retry count
interface AxiosErrorWithRetry extends AxiosError {
  config?: AxiosError["config"] & {
    __retryCount?: number;
  };
}

const baseUrl = configToUse.VITE_APP_API_BASE_URL;
const maxRetryCount = 2;

// Helper function to safely capture exceptions to PostHog without exposing sensitive data
const captureException = (params: {
  error: Error;
  exceptionType: string;
  errorContext: string;
  statusCode?: number;
  url?: string;
  method?: string;
}) => {
  const isAllowedHost = POSTHOG_ALLOWED_HOSTS.includes(window.location.hostname);
  if (isAllowedHost && posthog && typeof posthog.captureException === "function") {
    try {
      // Get user information from global store
      const userName = useGlobalStore.getState().userName;
      const userEmail = useGlobalStore.getState().userEmail;
      const userCompany = useGlobalStore.getState().userCompany;
      const userRole = useGlobalStore.getState().userRole;

      // Capture the actual error object with additional properties including user info
      posthog.captureException(params.error, {
        exception_type: params.exceptionType,
        error_context: params.errorContext,
        // User information
        user: {
          name: userName || "Unknown",
          email: userEmail || "Unknown",
          company: userCompany || "Unknown",
          role: userRole || "Unknown",
        },
        // Request information
        ...(params.statusCode && { status_code: params.statusCode }),
        ...(params.url && { url: params.url }),
        ...(params.method && { method: params.method }),
      });
    } catch (captureError) {
      console.error("Failed to capture exception to PostHog:", captureError);
    }
  }
};

// Helper function to show error toast notifications
const showErrorToast = (error: unknown) => {
  // Check if error has the expected error_object structure
  if (isAxiosError(error)) {
    const errorObject = (
      error.response?.data as { error_object?: { title: string; message: string } }
    ).error_object;
    if (errorObject) {
      toast.message(
        <ToastContent
          headingPrefix={errorObject.title}
          subheadingSuffix={errorObject.message}
          isError={true}
        />,
      );
      return;
    }
  }

  // Fallback: Show generic error message when error_object is not found
  toast.message(
    <ToastContent
      headingPrefix="Something went wrong"
      subheadingSuffix="An unexpected error occurred. Please try again."
      isError={true}
    />,
  );
};

const axiosInstance = axios.create({
  baseURL: baseUrl,
});

axiosInstance.interceptors.request.use(
  (config) => {
    const token = keycloak.token;
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    console.error("Request setup error:", error);
    captureException({
      error: error instanceof Error ? error : new Error(String(error)),
      exceptionType: "Request Setup Error",
      errorContext: "axios-request-interceptor",
    });
    return Promise.reject(error);
  },
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosErrorWithRetry) => {
    const config = error.config;

    if (error.response) {
      if (error.response.status === 401) {
        if (config) {
          if (!config.__retryCount) {
            config.__retryCount = 0;
          }

          if (config.__retryCount < maxRetryCount) {
            config.__retryCount += 1;

            try {
              await new Promise((resolve) => setTimeout(resolve, 1000)); // wait for 1 second before retrying
              return axiosInstance(config);
            } catch (retryError) {
              // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
              return Promise.reject(retryError);
            }
          }
        }

        // Show error toast for 401 errors
        showErrorToast(error);

        // Logout after max retries for 401 error
        logout("401_error");
      } else {
        // Show error toast for other errors with error_object structure
        showErrorToast(error);
        // Capture other HTTP errors
        captureException({
          error: error,
          exceptionType: "API Error",
          errorContext: `HTTP ${error.response.status} Error`,
          statusCode: error.response.status,
          url: config?.url,
          method: config?.method,
        });
      }
    } else {
      if (error.name !== "CanceledError") {
        captureException({
          error: error,
          exceptionType: "API Error",
          errorContext: error.request ? "Network Error" : "Request Error",
          url: config?.url,
          method: config?.method,
        });
      }
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
